"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Phone, MapPin, CircleCheck, Loader, Calendar, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
function EstimateForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="12345" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-accent/10 flex items-center justify-center">
          <CircleCheck className="size-10 text-accent" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground">Quote Request Sent!</h3>
          <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/5">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Clint Coastal" {...field} className="border-border focus-visible:ring-accent" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="folppclint430@gmail.com" {...field} className="border-border focus-visible:ring-accent" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="0413 771 815" {...field} className="border-border focus-visible:ring-accent" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="service_type" rules={{
        required: "Please select a service type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Service Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="h-12 border-border focus:ring-accent">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Residential">Residential Painting</SelectItem>
                  <SelectItem value="Industrial">Industrial / Commercial</SelectItem>
                  <SelectItem value="Interior">Interior Decoration</SelectItem>
                  <SelectItem value="Exterior">Exterior Protection</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="address" rules={{
      required: "Suburb is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Your Suburb</FormLabel>
            <FormControl>
              <Input placeholder="e.g., Gorokan, Toukley, or Norah Head" {...field} className="border-border focus-visible:ring-accent" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Preferred Start Date <span className="text-muted-foreground font-normal text-xs ml-1">(Optional)</span></FormLabel>
              <FormControl>
                <div className="relative">
                  <Input type="date" {...field} className="pl-10 border-border focus-visible:ring-accent" />
                  <SafeHydrate><Calendar className="absolute left-3 top-3 size-5 text-muted-foreground pointer-events-none" /></SafeHydrate>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
        
        <div className="hidden md:block">
           {}
        </div>
      </div>

      <FormField control={control} name="details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Project Details <span className="text-muted-foreground font-normal text-xs ml-1">(Optional)</span></FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about the surfaces, size, and any specific finishing requirements..." className="min-h-[120px] resize-y border-border focus-visible:ring-accent" {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground">
              Provide details about the scope of work for a more accurate professional quote.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <Info className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" size="lg" className="w-full md:w-auto min-w-[200px] text-base font-semibold h-12 bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg shadow-accent/20 transition-all" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : "Get My Free Quote"}
      </Button>
    </Form>;
}
export default function GetYourCustomEstimate() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="start-your-transformation" className="relative w-full bg-gradient-to-b from-white to-slate-50 border-t border-border" ref={ref}>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className={`flex flex-col lg:flex-row shadow-2xl rounded-2xl overflow-hidden transition-all duration-700 bg-white ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {}
          <div className="w-full lg:w-[60%] bg-white p-8 lg:p-12 order-2 lg:order-1">
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                Start Your Transformation
              </h2>
              <p className="text-muted-foreground text-lg">
                Tell us about your project and get a professional quote from the Central Coast's painting experts with over 30 years experience.
              </p>
            </div>

            <FormProvider formKey="quote_request_home" formId="quote_request_home" sectionName="start-your-transformation" defaultValues={{
            name: "",
            email: "",
            phone: "",
            service_type: "",
            address: "",
            details: "",
            preferred_date: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            service_type: {
              required: true
            },
            address: {
              required: true
            }
          }} submitText="Get My Free Quote" successMessage="Thank you! Clint will review your project details and get back to you with a professional quote shortly.">
              <EstimateForm />
            </FormProvider>
          </div>

          {}
          <div className="w-full lg:w-[40%] bg-primary text-primary-foreground p-8 lg:p-12 flex flex-col justify-between relative overflow-hidden order-1 lg:order-2">
            {}
            <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:24px_24px]"></div>
            
            <div className="relative z-10 space-y-10">
              <div>
                <Badge variant="outline" className="bg-accent/10 text-accent border-accent/50 mb-6 px-3 py-1 text-sm backdrop-blur-sm">
                  <span className="relative flex h-2 w-2 mr-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
                  </span>
                  Ready for a Fresh Look?
                </Badge>
                
                <h3 className="text-2xl font-bold text-white mb-2">Need Immediate Assistance?</h3>
                <p className="text-slate-300">
                  Clint is available to discuss your residential or industrial painting needs today.
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-semibold text-accent uppercase tracking-widest">Call Clint Now</p>
                <Link to="tel:0413771815" className="block text-4xl md:text-5xl font-bold text-white hover:text-accent transition-colors tracking-tighter">
                  0413 771 815
                </Link>
              </div>

              <div className="space-y-6 pt-6 border-t border-white/10">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10 shrink-0">
                    <MapPin className="size-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white">Service Area</h4>
                    <p className="text-slate-300 mt-1">
                      Northern Central Coast, NSW<br />
                      Gorokan, Toukley, Norah Head & surrounds
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10 shrink-0">
                    <Info className="size-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white">Business Hours</h4>
                    <p className="text-slate-300 mt-1">
                      Mon - Sat: 7:00 AM - 5:00 PM<br />
                      Closed Sundays
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-10 mt-12 pt-8">
              <p className="text-xs text-slate-400 font-medium tracking-wide">
                Clint's Coastal Painting & Decorating | ABN 54205050949 © {new Date().getFullYear()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}